<template>
  <div class="input-group flex-nowrap align-items-stretch" :class="{ 'opacity-75': isReloading }">
    <div class="input-group-prepend ms-wrapper" :class="[{ 'ms-wrapper--full': !reloadFn }]">
      <MultiSelect v-model="localModelValue" v-bind="$attrs" :options="localOptions" :loading="mergedLoading" @update:modelValue="onUpdateModelValue" @change="(e: any) => $emit('change', e)">
        <template v-for="(_, name) in $slots" v-slot:[name]="slotProps">
          <slot :name="name" v-bind="slotProps" />
        </template>
      </MultiSelect>
    </div>

    <span v-if="!!reloadFn" type="button" class="input-group-text" :class="buttonClass" v-geo-tooltip="reloadTitle" @click="handleReloadClick" :disabled="mergedButtonDisabled">
      <i class="uil uil-refresh" />
    </span>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
type OptionsSource = any[] | (() => any[] | Promise<any[]>);

export default defineComponent({
  name: 'MultiSelectReload',
  inheritAttrs: true,
  props: {
    modelValue: { type: [Object, Array, String, Number] as PropType<any>, default: null },
    options: { type: [Array, Function] as PropType<OptionsSource>, required: true },
    reloadFn: { type: Function as PropType<() => Promise<any[] | void> | any[] | void>, default: null },
    reloadTitle: { type: String, default: 'Recarregar opções' },
    buttonClass: { type: [String, Array, Object] as PropType<any>, default: '' }
  },
  emits: ['update:modelValue', 'reload', 'change'],
  data() {
    return {
      localModelValue: this.modelValue as any,
      localOptions: [] as any[],
      isReloading: false as boolean,
      isResolving: false as boolean
    };
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(v: any) {
        this.localModelValue = v;
      }
    },
    options: {
      immediate: true,
      async handler() {
        await this.resolveOptions();
      }
    }
  },
  computed: {
    parentDisabled(): boolean {
      return this.toBool(this.$attrs.disabled as unknown);
    },
    parentLoading(): boolean {
      return this.toBool(this.$attrs.loading as unknown);
    },
    mergedLoading(): boolean {
      return this.parentLoading || this.isReloading || this.isResolving;
    },
    mergedButtonDisabled(): boolean {
      return this.isReloading || this.isResolving || this.parentDisabled;
    }
  },
  mounted() {
    if (this.localOptions.length === 0) {
      this.resolveOptions();
    }
  },
  methods: {
    toBool(v: unknown): boolean {
      return v === '' || v === true || v === 'true';
    },
    isMultiple(): boolean {
      return this.toBool(this.$attrs.multiple as unknown);
    },
    onUpdateModelValue(v: any) {
      this.$emit('update:modelValue', v);
      this.$emit('change', v);
    },
    clearSelection() {
      const cleared = this.isMultiple() ? [] : null;
      this.localModelValue = cleared;
      this.$emit('update:modelValue', cleared);
      this.$emit('change', cleared);
    },
    async resolveOptions() {
      const src = this.options as OptionsSource;
      try {
        this.isResolving = true;
        if (typeof src === 'function') {
          const result = await src();
          this.localOptions = Array.isArray(result) ? result : [];
        } else {
          this.localOptions = Array.isArray(src) ? src : [];
        }
      } finally {
        this.isResolving = false;
      }
    },
    async handleReloadClick() {
      this.$emit('reload');
      if (!this.reloadFn) return;

      try {
        this.isReloading = true;
        const res = await this.reloadFn();
        if (Array.isArray(res)) {
          this.localOptions = res;
        } else {
          await this.resolveOptions();
        }
        this.clearSelection();
      } finally {
        this.isReloading = false;
      }
    }
  }
});
</script>

<style scoped>
.input-group {
  width: 100%;
}
.ms-wrapper {
  flex: 1 1 auto;
  min-width: 0;
}
.ms-wrapper--full {
  flex: 1 1 100%;
}
.ms-reload-btn {
  flex: 0 0 auto;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding-inline: 1rem;
}
.ms-wrapper :deep(.multiselect) {
  width: 100%;
}
@media (max-width: 768px) {
  .ms-reload-btn {
    padding-inline: 0.5rem;
  }
}
.btn-custom {
  display: flex;
  justify-content: center;
  align-items: center;
}
.opacity-75 {
  transition: opacity 0.2s ease;
}
</style>
